import React, { useState } from 'react';
import {
  apiService,
  SalesMonitoringAlert,
  SalesMonitoringAnalyzeResponse,
} from '../services/api';

const SalesMonitoringPage: React.FC = () => {
  // State
  const [currentAnalysis, setCurrentAnalysis] = useState<SalesMonitoringAnalyzeResponse | null>(null);
  const [alerts, setAlerts] = useState<SalesMonitoringAlert[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Parameters
  const [recentDays, setRecentDays] = useState(7);
  const [baselineDays, setBaselineDays] = useState(365);

  // Run new analysis
  const runNewAnalysis = async () => {
    setAnalyzing(true);
    setError(null);

    try {
      const result = await apiService.runSalesMonitoringAnalysis({
        recent_days: recentDays,
        baseline_days: baselineDays,
      });

      setCurrentAnalysis(result);
      setAlerts(result.alerts || []);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Erro ao executar análise';
      setError(errorMsg);
      console.error('Error running analysis:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  // Format date/time
  const formatDateTime = (isoString: string) => {
    try {
      return new Date(isoString).toLocaleString('pt-PT', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return isoString;
    }
  };

  // Filter alerts by type
  const dramaticIncreaseAlerts = alerts.filter(a => a.alert_type === 'dramatic_increase');
  const highAvgAlerts = alerts.filter(a => a.alert_type === 'high_avg');

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <h1>
              <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
              Monitorização de Vendas por Fatura
            </h1>
          </div>
        </div>
      </div>

      {/* Analysis Parameters */}
      <div className="row mb-4">
        <div className="col-md-3">
          <label className="form-label">Período Recente (dias):</label>
          <input
            type="number"
            className="form-control"
            value={recentDays}
            onChange={(e) => setRecentDays(parseInt(e.target.value))}
            min="1"
            max="90"
            disabled={analyzing}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">Período Baseline (dias):</label>
          <input
            type="number"
            className="form-control"
            value={baselineDays}
            onChange={(e) => setBaselineDays(parseInt(e.target.value))}
            min="30"
            max="730"
            disabled={analyzing}
          />
        </div>
        <div className="col-md-6 d-flex align-items-end">
          <button
            className="btn btn-primary w-100"
            onClick={runNewAnalysis}
            disabled={analyzing}
          >
            {analyzing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                A Analisar...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-clockwise me-2"></i>
                Executar Nova Análise
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="bi bi-exclamation-circle me-2"></i>
              <strong>Erro:</strong> {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {currentAnalysis && (
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h6 className="card-title small text-muted">Total de Alertas</h6>
                <p className="card-text display-6">{currentAnalysis.total_alerts}</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center bg-danger text-white">
              <div className="card-body">
                <h6 className="card-title small">Aumento Dramático (≥30%)</h6>
                <p className="card-text display-6">{currentAnalysis.dramatic_increase_count}</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center bg-warning">
              <div className="card-body">
                <h6 className="card-title small">Alta Média (≥10 un/fat)</h6>
                <p className="card-text display-6">{currentAnalysis.high_avg_count}</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center bg-info text-white">
              <div className="card-body">
                <h6 className="card-title small">Análise em:</h6>
                <p className="card-text small">{formatDateTime(currentAnalysis.analysis_timestamp)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dramatic Increase Table */}
      {dramaticIncreaseAlerts.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header bg-danger text-white">
                <h5 className="mb-0">
                  <i className="bi bi-graph-up-arrow me-2"></i>
                  Produtos com Aumento Dramático (≥30%)
                  <span className="badge bg-light text-danger ms-2">{dramaticIncreaseAlerts.length}</span>
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '12%' }}>SKU</th>
                        <th style={{ width: '20%' }}>Produto</th>
                        <th style={{ width: '12%' }}>Categoria</th>
                        <th style={{ width: '12%' }}>Marca</th>
                        <th className="text-end" style={{ width: '10%' }}>
                          Média Recente
                        </th>
                        <th className="text-end" style={{ width: '10%' }}>
                          Média 365d
                        </th>
                        <th className="text-end" style={{ width: '10%' }}>
                          Variação
                        </th>
                        <th className="text-center" style={{ width: '8%' }}>
                          Faturas
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dramaticIncreaseAlerts.map((alert) => (
                        <tr key={alert.sku} className="align-middle">
                          <td>
                            <a href={`/products/${alert.sku}`} target="_blank" rel="noreferrer" className="text-decoration-none">
                              <code className="text-danger">{alert.sku}</code>
                            </a>
                          </td>
                          <td className="text-truncate" title={alert.product_name || ''}>
                            {alert.product_name || '-'}
                          </td>
                          <td>{alert.categoria_nome || '-'}</td>
                          <td>{alert.marca_nome || '-'}</td>
                          <td className="text-end">
                            <strong className="text-danger">
                              {alert.avg_units_per_invoice_recent.toFixed(2)} un/fat
                            </strong>
                          </td>
                          <td className="text-end">{alert.avg_units_per_invoice_baseline.toFixed(2)} un/fat</td>
                          <td className="text-end">
                            <span className="badge bg-danger" title={`${alert.avg_change_pct.toFixed(1)}%`}>
                              +{Math.round(alert.avg_change_pct)}%
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-light text-dark">
                              {alert.total_invoices_recent}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* High Average Table */}
      {highAvgAlerts.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header bg-warning">
                <h5 className="mb-0">
                  <i className="bi bi-clipboard-data me-2"></i>
                  Produtos com Alta Média (≥10 un/fatura)
                  <span className="badge bg-light text-warning ms-2">{highAvgAlerts.length}</span>
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '12%' }}>SKU</th>
                        <th style={{ width: '20%' }}>Produto</th>
                        <th style={{ width: '12%' }}>Categoria</th>
                        <th style={{ width: '12%' }}>Marca</th>
                        <th className="text-end" style={{ width: '10%' }}>
                          Média Recente
                        </th>
                        <th className="text-end" style={{ width: '10%' }}>
                          Média 365d
                        </th>
                        <th className="text-end" style={{ width: '10%' }}>
                          Variação
                        </th>
                        <th className="text-center" style={{ width: '8%' }}>
                          Faturas
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {highAvgAlerts.map((alert) => (
                        <tr key={alert.sku} className="align-middle">
                          <td>
                            <a href={`/products/${alert.sku}`} target="_blank" rel="noreferrer" className="text-decoration-none">
                              <code className="text-warning">{alert.sku}</code>
                            </a>
                          </td>
                          <td className="text-truncate" title={alert.product_name || ''}>
                            {alert.product_name || '-'}
                          </td>
                          <td>{alert.categoria_nome || '-'}</td>
                          <td>{alert.marca_nome || '-'}</td>
                          <td className="text-end">
                            <strong className="text-warning">
                              {alert.avg_units_per_invoice_recent.toFixed(2)} un/fat
                            </strong>
                          </td>
                          <td className="text-end">{alert.avg_units_per_invoice_baseline.toFixed(2)} un/fat</td>
                          <td className="text-end">
                            <span className="badge bg-warning text-dark" title={`${alert.avg_change_pct.toFixed(1)}%`}>
                              {alert.avg_change_pct > 0 ? '+' : ''}{Math.round(alert.avg_change_pct)}%
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-light text-dark">
                              {alert.total_invoices_recent}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!currentAnalysis && !analyzing && (
        <div className="row">
          <div className="col-12">
            <div className="alert alert-info text-center">
              <i className="bi bi-info-circle me-2"></i>
              Clique em "Executar Nova Análise" para detetar anomalias nas vendas por fatura.
            </div>
          </div>
        </div>
      )}

      {/* No alerts state */}
      {currentAnalysis && currentAnalysis.total_alerts === 0 && (
        <div className="row">
          <div className="col-12">
            <div className="alert alert-success text-center">
              <i className="bi bi-check-circle me-2"></i>
              Sem alertas! Todos os produtos têm métricas de vendas por fatura normais.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesMonitoringPage;
